import { useEffect, useState, useRef } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

export interface FingerprintState {
  visitorId: string | null;
  components: Record<string, unknown> | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to get device fingerprint using FingerprintJS OSS.
 * Note: OSS version has 40-60% accuracy. Upgrade to Pro for 99.5%.
 */
export function useFingerprint() {
  const [state, setState] = useState<FingerprintState>({
    visitorId: null,
    components: null,
    isLoading: true,
    error: null,
  });
  const initAttempted = useRef(false);

  useEffect(() => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    // Timeout to prevent blocking forever (3 seconds - fail fast)
    const timeoutId = setTimeout(() => {
      console.warn('Fingerprint timeout - proceeding without fingerprint');
      setState((prev) => {
        if (prev.isLoading) {
          return {
            visitorId: null,
            components: null,
            isLoading: false,
            error: 'Fingerprint timeout',
          };
        }
        return prev;
      });
    }, 3000);

    const getFingerprint = async () => {
      try {
        // Load the FingerprintJS agent
        const fp = await FingerprintJS.load();

        // Get the visitor identifier
        const result = await fp.get();

        clearTimeout(timeoutId);
        setState({
          visitorId: result.visitorId,
          components: result.components,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        clearTimeout(timeoutId);
        console.warn('Fingerprint generation failed:', error);
        setState({
          visitorId: null,
          components: null,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    getFingerprint();

    return () => clearTimeout(timeoutId);
  }, []);

  return state;
}
